import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  LayersControl,
  useMapEvents,
  ScaleControl,
  Polyline,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Live, MapSpot } from "@/common/model/Live";
import { ViewMode, SpotRequest } from "@/common/model/SpotRequest";
import { ENDPOINTS } from "@/constants/api";
import { getDaylightInfo } from "@/utils/sunCalc";
import { LiveService } from "@/services/LiveService";
import SpotManagerModal from "./SpotManagerModal";
import { RequestMapMarkers } from "./requests";
import { useAuth } from "@/context/AuthContext";

// Default profile picture as a data URL (gray circle with user icon)
const DEFAULT_PROFILE_PIC = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%236b7280'%3E%3Ccircle cx='12' cy='12' r='12' fill='%231f2937'/%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' fill='%236b7280'/%3E%3C/svg%3E";

// Color function based on max jump height (meters) - matching app pin colors
const getColorByHeight = (height: number): string => {
  if (height < 5)  return "#00E676";  // pin1 - Green
  if (height < 8)  return "#FFEB3B";  // pin2 - Yellow
  if (height < 12) return "#FF9800";  // pin3 - Orange
  if (height < 15) return "#FF5252";  // pin4 - Red
  if (height < 20) return "#FF4081";  // pin5 - Pink
  return "#7C4DFF";                    // pin6 - Purple (20m+)
};

// Darker color variants for clusters (better text readability)
const getClusterColorByHeight = (height: number): string => {
  if (height < 5)  return "#00A846";  // Dark Green
  if (height < 8)  return "#E6A800";  // Dark Yellow/Gold
  if (height < 12) return "#E65100";  // Dark Orange
  if (height < 15) return "#C62828";  // Dark Red
  if (height < 20) return "#AD1457";  // Dark Pink
  return "#5E35B1";                    // Dark Purple (20m+)
};

// Track which profile images have failed to load
const failedImageIds = new Set<string>();

// Fix for default marker icons in webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export interface SpotSettings {
  showSpots: boolean;
  minZoomLevel: number;
}

export interface FlyToTarget {
  lat: number;
  lng: number;
  zoom?: number;
}

interface LiveMapProps {
  sessions: Live[];
  selectedSessionId: number | null;
  selectionSource: "map" | "list" | null;
  hoveredSessionId: number | null;
  onSessionSelect: (sessionId: number) => void;
  onSessionHover: (sessionId: number | null) => void;
  onShowDetails: (session: Live) => void;
  spotSettings: SpotSettings;
  onSpotSettingsChange: (settings: SpotSettings) => void;
  flyToTarget?: FlyToTarget | null;
  onFlyToComplete?: () => void;
  // Request mode props
  viewMode?: ViewMode;
  spotRequests?: SpotRequest[];
  selectedRequest?: SpotRequest | null;
  hoveredRequest?: SpotRequest | null;
  onRequestSelect?: (request: SpotRequest) => void;
  onReviewRequest?: (request: SpotRequest) => void;
  onSwapRequest?: (request: SpotRequest) => void;
  // Add spot mode props
  addSpotMode?: boolean;
  addSpotLocation?: { lat: number; lng: number } | null;
  addSpotNearbySpots?: Array<{ id: number; name: string; latitude: number; longitude: number; distance: number }>;
  onMapClickForSpot?: (lat: number, lng: number) => void;
  onAddSpotDrag?: (lat: number, lng: number) => void;
}

// Custom hook to handle map updates
const MapUpdater: React.FC<{
  sessions: Live[];
  selectedSessionId: number | null;
  selectionSource: "map" | "list" | null;
  hoveredSessionId: number | null;
  clusterGroupRef: React.RefObject<any>;
  markerRefs: React.MutableRefObject<Map<number, L.Marker>>;
  flyToTarget?: FlyToTarget | null;
  onFlyToComplete?: () => void;
}> = ({ sessions, selectedSessionId, selectionSource, hoveredSessionId, clusterGroupRef, markerRefs, flyToTarget, onFlyToComplete }) => {
  const map = useMap();
  const initialLoadDone = useRef(false);
  const prevSelectedId = useRef<number | null>(null);
  const prevHoveredId = useRef<number | null>(null);

  // Handle flyToTarget (for spot selection from search)
  useEffect(() => {
    if (flyToTarget) {
      map.flyTo([flyToTarget.lat, flyToTarget.lng], flyToTarget.zoom ?? 14, {
        duration: 1,
      });
      // Call completion callback after animation
      if (onFlyToComplete) {
        setTimeout(onFlyToComplete, 1100);
      }
    }
  }, [flyToTarget, map, onFlyToComplete]);

  // Fly to selected session (only when selection comes from list, not map)
  useEffect(() => {
    if (selectedSessionId && selectedSessionId !== prevSelectedId.current) {
      const session = sessions.find((s) => s.id === selectedSessionId);
      if (session && session.lastknownlat && session.lastknownlong) {
        // Only fly/zoom when selection comes from the list
        if (selectionSource === "list") {
          map.flyTo([session.lastknownlat, session.lastknownlong], 14, {
            duration: 1,
          });

          // Try to open popup after fly animation completes
          setTimeout(() => {
            const marker = markerRefs.current.get(selectedSessionId);
            if (marker) {
              try {
                marker.openPopup();
              } catch (e) {
                // Marker might be in cluster, ignore error
              }
            }
          }, 1100);
        }
        // When selection comes from map, popup is already open via marker click
      }
    }
    prevSelectedId.current = selectedSessionId;
  }, [selectedSessionId, selectionSource, sessions, map, markerRefs]);

  // Handle hover - highlight cluster if marker is clustered
  useEffect(() => {
    // Reset all highlighted clusters
    const resetHighlightedClusters = () => {
      const allClusters = document.querySelectorAll('.custom-cluster-marker');
      allClusters.forEach((el) => {
        const htmlEl = el as HTMLElement;
        if (htmlEl.dataset.highlighted === 'true') {
          // Reset the inner div styles
          const innerDiv = htmlEl.querySelector('div') as HTMLElement;
          if (innerDiv) {
            innerDiv.style.transform = '';
            innerDiv.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
            innerDiv.style.border = '3px solid white';
          }
          htmlEl.dataset.highlighted = '';
        }
      });
    };

    // Reset previous hover cluster highlight when hover changes
    if (prevHoveredId.current !== hoveredSessionId) {
      resetHighlightedClusters();
    }

    if (hoveredSessionId) {
      const session = sessions.find(s => s.id === hoveredSessionId);
      if (session && session.lastknownlat && session.lastknownlong) {
        // Small delay to ensure cluster group is ready
        setTimeout(() => {
          try {
            // Try to get the cluster group - handle different ref structures
            let clusterGroup: any = clusterGroupRef.current;

            // Debug: log the ref structure
            console.log('ClusterGroup ref:', clusterGroup);
            console.log('ClusterGroup keys:', clusterGroup ? Object.keys(clusterGroup) : 'null');

            // react-leaflet-cluster might wrap the actual cluster group
            if (clusterGroup && !clusterGroup.getLayers && clusterGroup._group) {
              clusterGroup = clusterGroup._group;
              console.log('Using _group:', clusterGroup);
            }

            if (!clusterGroup || !clusterGroup.getLayers) {
              console.log('No getLayers method found');
              return;
            }

            // Get all layers from the cluster group
            const layers = clusterGroup.getLayers();
            console.log('Layers count:', layers.length);

            // Find the marker by matching coordinates
            let targetMarker: any = null;
            for (const layer of layers) {
              if (layer.getLatLng) {
                const latLng = layer.getLatLng();
                if (Math.abs(latLng.lat - session.lastknownlat) < 0.0001 &&
                    Math.abs(latLng.lng - session.lastknownlong) < 0.0001) {
                  targetMarker = layer;
                  break;
                }
              }
            }

            // Also try marker refs as backup
            if (!targetMarker) {
              targetMarker = markerRefs.current.get(hoveredSessionId);
              console.log('Using marker ref:', targetMarker);
            }

            console.log('Target marker found:', !!targetMarker);
            console.log('getVisibleParent exists:', !!clusterGroup.getVisibleParent);

            if (targetMarker && clusterGroup.getVisibleParent) {
              // Get the visible parent (cluster or marker itself)
              const visibleParent = clusterGroup.getVisibleParent(targetMarker);
              console.log('Visible parent:', visibleParent);
              console.log('Is cluster (different from marker):', visibleParent !== targetMarker);

              // Check if the visible parent is a cluster (different from the marker)
              if (visibleParent && visibleParent !== targetMarker) {
                // It's a cluster - try to get its element
                const clusterEl = visibleParent.getElement ? visibleParent.getElement() : null;
                console.log('Cluster element:', clusterEl);

                if (clusterEl) {
                  // Apply highlight styles to the inner div
                  const innerDiv = clusterEl.querySelector('div') as HTMLElement;
                  console.log('Inner div:', innerDiv);

                  if (innerDiv) {
                    innerDiv.style.transform = 'scale(1.25)';
                    innerDiv.style.transition = 'all 0.2s ease';
                    innerDiv.style.boxShadow = '0 0 20px 8px rgba(59, 130, 246, 0.8), 0 0 40px 16px rgba(59, 130, 246, 0.5)';
                    innerDiv.style.border = '4px solid #60a5fa';
                    console.log('Applied highlight styles!');
                  }
                  clusterEl.dataset.highlighted = 'true';
                }
              }
            }
          } catch (e) {
            // Silently ignore errors - cluster might not be ready
          }
        }, 100);
      }
    }

    prevHoveredId.current = hoveredSessionId;
  }, [hoveredSessionId, sessions, clusterGroupRef, markerRefs]);

  // Fit bounds to show all sessions only on initial load
  useEffect(() => {
    if (!initialLoadDone.current && sessions.length > 0) {
      const validSessions = sessions.filter(
        (s) => s.lastknownlat && s.lastknownlong && s.lastknownlat !== 0
      );
      if (validSessions.length > 0) {
        const bounds = L.latLngBounds(
          validSessions.map((s) => [s.lastknownlat, s.lastknownlong])
        );
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 10 });
        initialLoadDone.current = true;
      }
    }
  }, [sessions, map]);

  return null;
};

// Create custom marker icon with profile picture and height-based color ring
const createProfileMarkerIcon = (
  session: Live,
  isSelected: boolean,
  isHovered: boolean
) => {
  const size = isSelected ? 50 : isHovered ? 45 : 40;
  const heightColor = getColorByHeight(session.maxheight);

  // Ring styling: height color as outer ring, selection/hover affects inner border
  const ringWidth = 4;
  const innerBorderColor = isSelected
    ? "#3b82f6"
    : isHovered
    ? "#60a5fa"
    : "white";
  const innerBorderWidth = isSelected ? 3 : isHovered ? 2 : 2;

  // Add glow effect for higher jumps
  const glowIntensity = session.maxheight >= 15 ? "0 0 12px" : session.maxheight >= 8 ? "0 0 8px" : "0 0 4px";
  const boxShadow = `${glowIntensity} ${heightColor}, 0 2px 8px rgba(0,0,0,0.3)`;

  // Check if this profile image has failed before
  const profilePicId = session.user.profilepicid;
  const hasFailed = profilePicId ? failedImageIds.has(profilePicId) : true;

  const profileUrl = !profilePicId || hasFailed
    ? DEFAULT_PROFILE_PIC
    : `${ENDPOINTS.IMAGES.PROFILE}/${profilePicId}`;

  // Generate unique ID for error handling
  const imgId = `marker-img-${session.id}`;

  // Calculate inner image size
  const innerSize = size - (ringWidth * 2) - (innerBorderWidth * 2);

  return L.divIcon({
    className: "custom-marker",
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        position: relative;
      ">
        <!-- Outer height-color ring -->
        <div style="
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background: ${heightColor};
          padding: ${ringWidth}px;
          box-shadow: ${boxShadow};
          box-sizing: border-box;
        ">
          <!-- Inner container with profile image -->
          <div style="
            width: 100%;
            height: 100%;
            border-radius: 50%;
            border: ${innerBorderWidth}px solid ${innerBorderColor};
            overflow: hidden;
            background: #1f2937;
            box-sizing: border-box;
          ">
            <img
              id="${imgId}"
              src="${profileUrl}"
              style="width: 100%; height: 100%; object-fit: cover;"
              onerror="this.onerror=null; this.src='${DEFAULT_PROFILE_PIC}';"
            />
          </div>
        </div>
        ${
          isSelected || isHovered
            ? `<div style="
            position: absolute;
            bottom: -20px;
            left: 50%;
            transform: translateX(-50%);
            background: ${isSelected ? "#3b82f6" : "#374151"};
            color: white;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            white-space: nowrap;
            box-shadow: 0 1px 4px rgba(0,0,0,0.3);
          ">${session.user.name}</div>`
            : ""
        }
        ${
          session.isstreaming
            ? `<div style="
            position: absolute;
            top: -4px;
            right: -4px;
            background: #ef4444;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
              <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
            </svg>
          </div>`
            : ""
        }
      </div>
    `,
    iconSize: [size, size + (isSelected || isHovered ? 20 : 0)],
    iconAnchor: [size / 2, size / 2],
  });
};

// Create a coordinate key for reliable lookup (rounds to 5 decimal places)
const getCoordKey = (lat: number, lng: number): string => {
  return `${lat.toFixed(5)}_${lng.toFixed(5)}`;
};

// Custom cluster icon with height-based color
const createClusterIcon = (cluster: any, sessionHeightMap: Map<string, number>) => {
  const count = cluster.getChildCount();
  let size = 40;

  if (count > 10) {
    size = 50;
  }
  if (count > 25) {
    size = 60;
  }

  // Get all markers in this cluster and find max height
  const childMarkers = cluster.getAllChildMarkers();
  let maxHeight = 0;

  childMarkers.forEach((marker: any) => {
    const latLng = marker.getLatLng();
    const key = getCoordKey(latLng.lat, latLng.lng);
    const height = sessionHeightMap.get(key);
    if (height !== undefined && height > maxHeight) {
      maxHeight = height;
    }
  });

  const bgColor = getClusterColorByHeight(maxHeight);

  // Add glow for high-jump clusters (using brighter original color for glow)
  const glowColor = getColorByHeight(maxHeight);
  const glowIntensity = maxHeight >= 15 ? "0 0 15px" : maxHeight >= 8 ? "0 0 10px" : "0 0 6px";
  const boxShadow = `${glowIntensity} ${glowColor}, 0 2px 8px rgba(0,0,0,0.3)`;

  // Text color: always white on darker backgrounds
  const textColor = "white";

  // Larger sizes for better readability
  const displaySize = size + 8;

  return L.divIcon({
    html: `
      <div style="
        width: ${displaySize}px;
        height: ${displaySize}px;
        background: ${bgColor};
        border-radius: 50%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: ${textColor};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        border: 3px solid white;
        box-shadow: ${boxShadow};
      ">
        <span style="
          font-size: ${displaySize > 55 ? 18 : 16}px;
          font-weight: 700;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          line-height: 1.1;
        ">${count}</span>
        <span style="
          font-size: ${displaySize > 55 ? 11 : 10}px;
          font-weight: 600;
          text-shadow: 0 1px 2px rgba(0,0,0,0.5);
          opacity: 0.95;
          line-height: 1.1;
        ">${maxHeight.toFixed(1)}m</span>
      </div>
    `,
    className: "custom-cluster-marker",
    iconSize: L.point(displaySize, displaySize),
  });
};

// Create spot marker icon (pin style - different from user markers) with label
const createSpotMarkerIcon = (spotName: string, isHovered: boolean = false) => {
  const size = isHovered ? 28 : 24;
  const pinColor = isHovered ? "#dc2626" : "#ef4444"; // red-600/500
  const labelMaxWidth = 100;
  // Truncate name if too long
  const displayName = spotName.length > 15 ? spotName.substring(0, 14) + "…" : spotName;

  return L.divIcon({
    className: "spot-marker",
    html: `
      <div style="
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        z-index: 100;
      ">
        <svg viewBox="0 0 24 32" width="${size}" height="${size + 6}" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
          <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="${pinColor}"/>
          <circle cx="12" cy="11" r="5" fill="white"/>
          <path d="M12 8c-1.7 0-3 1.3-3 3s1.3 3 3 3 3-1.3 3-3-1.3-3-3-3zm0 4.5c-.8 0-1.5-.7-1.5-1.5s.7-1.5 1.5-1.5 1.5.7 1.5 1.5-.7 1.5-1.5 1.5z" fill="${pinColor}"/>
        </svg>
        <div style="
          background: rgba(255, 255, 255, 0.95);
          padding: 1px 4px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 500;
          color: #374151;
          white-space: nowrap;
          max-width: ${labelMaxWidth}px;
          overflow: hidden;
          text-overflow: ellipsis;
          box-shadow: 0 1px 2px rgba(0,0,0,0.2);
          margin-top: -2px;
          border: 1px solid ${isHovered ? '#dc2626' : '#fecaca'};
        ">${displayName}</div>
      </div>
    `,
    iconSize: [labelMaxWidth, size + 24],
    iconAnchor: [labelMaxWidth / 2, size + 6],
    popupAnchor: [0, -(size + 2)],
  });
};

// Component to handle spot loading based on map bounds and zoom
const SpotLoader: React.FC<{
  onSpotsLoaded: (spots: MapSpot[]) => void;
  minZoomLevel: number;
  showSpots: boolean;
}> = ({ onSpotsLoaded, minZoomLevel, showSpots }) => {
  const map = useMap();
  const lastBoundsRef = useRef<string>("");
  const loadingRef = useRef(false);

  const loadSpots = useCallback(async () => {
    if (!showSpots || loadingRef.current) return;

    const zoom = map.getZoom();
    if (zoom < minZoomLevel) {
      onSpotsLoaded([]);
      return;
    }

    const bounds = map.getBounds();
    const boundsKey = `${bounds.getNorth().toFixed(3)},${bounds.getSouth().toFixed(3)},${bounds.getEast().toFixed(3)},${bounds.getWest().toFixed(3)},${zoom}`;

    // Skip if bounds haven't changed significantly
    if (boundsKey === lastBoundsRef.current) return;
    lastBoundsRef.current = boundsKey;

    loadingRef.current = true;
    try {
      const center = bounds.getCenter();
      const latDelta = bounds.getNorth() - bounds.getSouth();
      const lonDelta = bounds.getEast() - bounds.getWest();

      const spots = await LiveService.getSpotsForMap(
        center.lat,
        center.lng,
        latDelta * 1.2, // Slightly larger to preload
        lonDelta * 1.2,
        300
      );
      onSpotsLoaded(spots);
    } catch (error) {
      console.error("Error loading spots:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [map, minZoomLevel, showSpots, onSpotsLoaded]);

  useMapEvents({
    moveend: loadSpots,
    zoomend: loadSpots,
  });

  // Initial load
  useEffect(() => {
    const timer = setTimeout(loadSpots, 500);
    return () => clearTimeout(timer);
  }, [loadSpots]);

  return null;
};

// Measurement tool component
const MeasurementTool: React.FC<{
  isActive: boolean;
  onToggle: () => void;
}> = ({ isActive, onToggle }) => {
  const [points, setPoints] = useState<L.LatLng[]>([]);
  const [totalDistance, setTotalDistance] = useState(0);

  // Calculate distance between two points using Haversine formula
  const calculateDistance = useCallback((p1: L.LatLng, p2: L.LatLng): number => {
    return p1.distanceTo(p2); // Leaflet's built-in method returns meters
  }, []);

  // Calculate total distance
  useEffect(() => {
    if (points.length < 2) {
      setTotalDistance(0);
      return;
    }
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      total += calculateDistance(points[i - 1], points[i]);
    }
    setTotalDistance(total);
  }, [points, calculateDistance]);

  // Handle map clicks when measurement is active
  useMapEvents({
    click: (e) => {
      if (isActive) {
        setPoints((prev) => [...prev, e.latlng]);
      }
    },
  });

  // Clear measurements when deactivated
  useEffect(() => {
    if (!isActive) {
      setPoints([]);
      setTotalDistance(0);
    }
  }, [isActive]);

  // Format distance for display
  const formatDistance = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.round(meters)} m`;
  };

  // Create markers for measurement points
  const createMeasurePointIcon = (index: number, isLast: boolean) => {
    return L.divIcon({
      className: "measure-point",
      html: `
        <div style="
          width: ${isLast ? 14 : 10}px;
          height: ${isLast ? 14 : 10}px;
          background: ${isLast ? '#ef4444' : '#3b82f6'};
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        "></div>
      `,
      iconSize: [isLast ? 14 : 10, isLast ? 14 : 10],
      iconAnchor: [isLast ? 7 : 5, isLast ? 7 : 5],
    });
  };

  return (
    <>
      {/* Control button */}
      <div
        style={{
          position: "absolute",
          top: "80px",
          right: "10px",
          zIndex: 1000,
        }}
      >
        <button
          onClick={onToggle}
          title={isActive ? "Stop measuring (ESC)" : "Measure distance"}
          style={{
            width: "34px",
            height: "34px",
            backgroundColor: isActive ? "#3b82f6" : "white",
            border: "2px solid rgba(0,0,0,0.2)",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 5px rgba(0,0,0,0.4)",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isActive ? "white" : "#333"}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21.3 15.3l-5.3-5.3 3-3-6-6-3 3-5.3-5.3-1.4 1.4 5.3 5.3-3 3 6 6 3-3 5.3 5.3z" />
          </svg>
        </button>
      </div>

      {/* Distance display panel */}
      {isActive && (
        <div
          style={{
            position: "absolute",
            top: "120px",
            right: "10px",
            zIndex: 1000,
            backgroundColor: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            minWidth: "120px",
          }}
        >
          <div style={{ fontSize: "10px", color: "#6b7280", marginBottom: "4px" }}>
            DISTANCE
          </div>
          <div style={{ fontSize: "16px", fontWeight: 600, color: "#111827" }}>
            {formatDistance(totalDistance)}
          </div>
          {points.length > 0 && (
            <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>
              {points.length} point{points.length !== 1 ? "s" : ""} •{" "}
              <button
                onClick={() => setPoints([])}
                style={{
                  color: "#ef4444",
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Clear
              </button>
            </div>
          )}
          <div style={{ fontSize: "9px", color: "#9ca3af", marginTop: "6px", borderTop: "1px solid #e5e7eb", paddingTop: "6px" }}>
            Click map to add points
          </div>
        </div>
      )}

      {/* Measurement line */}
      {points.length >= 2 && (
        <Polyline
          positions={points.map((p) => [p.lat, p.lng])}
          pathOptions={{
            color: "#3b82f6",
            weight: 3,
            opacity: 0.8,
            dashArray: "10, 10",
          }}
        />
      )}

      {/* Measurement point markers */}
      {points.map((point, index) => (
        <Marker
          key={`measure-${index}`}
          position={point}
          icon={createMeasurePointIcon(index, index === points.length - 1)}
          eventHandlers={{
            click: (e) => {
              // Remove point on click
              e.originalEvent.stopPropagation();
              setPoints((prev) => prev.filter((_, i) => i !== index));
            },
          }}
        />
      ))}
    </>
  );
};

// Click handler for add-spot mode
const AddSpotMapClickHandler: React.FC<{
  onClick?: (lat: number, lng: number) => void;
}> = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      onClick?.(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const LiveMap: React.FC<LiveMapProps> = ({
  sessions,
  selectedSessionId,
  selectionSource,
  hoveredSessionId,
  onSessionSelect,
  onSessionHover,
  onShowDetails,
  spotSettings,
  onSpotSettingsChange,
  flyToTarget,
  onFlyToComplete,
  // Request mode props
  viewMode = "riders",
  spotRequests = [],
  selectedRequest = null,
  hoveredRequest = null,
  onRequestSelect,
  onReviewRequest,
  onSwapRequest,
  // Add spot mode props
  addSpotMode = false,
  addSpotLocation = null,
  addSpotNearbySpots = [],
  onMapClickForSpot,
  onAddSpotDrag,
}) => {
  const clusterGroupRef = useRef<any>(null);
  const markerRefs = useRef<Map<number, L.Marker>>(new Map());
  const sessionHeightMapRef = useRef<Map<string, number>>(new Map());
  const [spots, setSpots] = useState<MapSpot[]>([]);
  const [hoveredSpotId, setHoveredSpotId] = useState<number | null>(null);
  const [spotRidersModal, setSpotRidersModal] = useState<{ spot: MapSpot; riders: Live[] } | null>(null);
  const [spotManagerModal, setSpotManagerModal] = useState<MapSpot | null>(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const { user } = useAuth();

  const handleSpotsLoaded = useCallback((loadedSpots: MapSpot[]) => {
    setSpots(loadedSpots);
  }, []);

  const validSessions = useMemo(
    () =>
      sessions.filter(
        (s) => s.lastknownlat && s.lastknownlong && s.lastknownlat !== 0
      ),
    [sessions]
  );

  // Create a map of coordinate keys to max heights for reliable cluster lookup
  // Store in ref so the iconCreateFunction closure always has access to latest data
  useEffect(() => {
    const map = new Map<string, number>();
    validSessions.forEach((s) => {
      const key = getCoordKey(s.lastknownlat, s.lastknownlong);
      map.set(key, s.maxheight);
    });
    sessionHeightMapRef.current = map;

    // Force cluster icons to refresh when data changes
    if (clusterGroupRef.current && map.size > 0) {
      try {
        clusterGroupRef.current.refreshClusters();
      } catch (e) {
        // refreshClusters might not exist, ignore
      }
    }
  }, [validSessions]);

  const defaultCenter: [number, number] = [30, 0];
  const defaultZoom = 2;

  const formatHeight = (height: number) => height.toFixed(1);

  const formatSessionTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Store marker ref
  const setMarkerRef = (sessionId: number, marker: L.Marker | null) => {
    if (marker) {
      markerRefs.current.set(sessionId, marker);
    } else {
      markerRefs.current.delete(sessionId);
    }
  };

  return (
    <MapContainer
      center={defaultCenter}
      zoom={defaultZoom}
      style={{ height: "100%", width: "100%" }}
      className="rounded-lg"
    >
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Street">
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            attribution='Tiles &copy; Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Terrain">
          <TileLayer
            attribution='&copy; OpenTopoMap'
            url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
          />
        </LayersControl.BaseLayer>
        <LayersControl.Overlay name="Labels">
          <TileLayer
            attribution='Labels &copy; Esri'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
          />
        </LayersControl.Overlay>
      </LayersControl>

      {/* Scale Control - shows distance scale on the map */}
      <ScaleControl position="bottomleft" imperial={false} />

      {/* Measurement Tool */}
      <MeasurementTool
        isActive={isMeasuring}
        onToggle={() => setIsMeasuring((prev) => !prev)}
      />

      <MapUpdater
        sessions={sessions}
        selectedSessionId={selectedSessionId}
        selectionSource={selectionSource}
        hoveredSessionId={hoveredSessionId}
        clusterGroupRef={clusterGroupRef}
        markerRefs={markerRefs}
        flyToTarget={flyToTarget}
        onFlyToComplete={onFlyToComplete}
      />

      {/* Session Markers (Riders mode only) */}
      {viewMode === "riders" && (
      <MarkerClusterGroup
        ref={clusterGroupRef}
        chunkedLoading
        iconCreateFunction={(cluster: any) => createClusterIcon(cluster, sessionHeightMapRef.current)}
        spiderfyOnMaxZoom={true}
        showCoverageOnHover={false}
        maxClusterRadius={60}
      >
        {validSessions.map((session) => {
          const isSelected = selectedSessionId === session.id;
          const isHovered = hoveredSessionId === session.id;
          return (
          <Marker
            key={session.id}
            position={[session.lastknownlat, session.lastknownlong]}
            icon={createProfileMarkerIcon(session, isSelected, isHovered)}
            ref={(ref) => setMarkerRef(session.id, ref as unknown as L.Marker)}
            eventHandlers={{
              click: () => onSessionSelect(session.id),
              mouseover: () => onSessionHover(session.id),
              mouseout: () => onSessionHover(null),
            }}
          >
            <Popup className="custom-popup" maxWidth={300}>
              <div className="p-2">
                {/* User Header */}
                <div className="flex items-center gap-3 mb-3">
                  <img
                    src={
                      session.user.profilepicid && !failedImageIds.has(session.user.profilepicid)
                        ? `${ENDPOINTS.IMAGES.PROFILE}/${session.user.profilepicid}`
                        : DEFAULT_PROFILE_PIC
                    }
                    alt={session.user.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-300"
                    onError={(e) => {
                      if (session.user.profilepicid) {
                        failedImageIds.add(session.user.profilepicid);
                      }
                      (e.target as HTMLImageElement).src = DEFAULT_PROFILE_PIC;
                    }}
                  />
                  <div>
                    <div className="font-bold text-gray-900 flex items-center gap-2">
                      {session.user.name}
                      {session.user.ispro && (
                        <span className="bg-yellow-500 text-xs font-bold px-1 rounded text-black">
                          PRO
                        </span>
                      )}
                      {session.isstreaming && (
                        <span className="bg-red-500 text-xs font-bold px-1 rounded text-white">
                          LIVE
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{session.user.username}</div>
                  </div>
                </div>

                {/* Spot & Local Time */}
                <div className="bg-gray-100 rounded-lg p-2 mb-3">
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-medium text-gray-700 flex-1">
                      {session.spot?.name || "Unknown location"}
                    </div>
                    {session.spot?.alpha2 && (
                      <img
                        src={`https://flagcdn.com/w20/${session.spot.alpha2.toLowerCase()}.png`}
                        alt={session.spot.alpha2}
                        className="w-5 h-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                  </div>
                  {/* Local Time */}
                  {(() => {
                    const daylightInfo = getDaylightInfo(session.lastknownlat, session.lastknownlong);
                    return (
                      <div className={`mt-2 flex items-center gap-2 text-xs ${
                        daylightInfo.isDaylight ? "text-amber-600" : "text-blue-600"
                      }`}>
                        {daylightInfo.isDaylight ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                          </svg>
                        )}
                        <span className="font-medium">Local: {daylightInfo.localTimeString}</span>
                        <span className="text-gray-500">
                          ({daylightInfo.isDaylight ? `sunset ${daylightInfo.sunsetTime}` : `sunrise ${daylightInfo.sunriseTime}`})
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-2 text-center mb-3">
                  <div
                    className="bg-gray-100 rounded-lg p-2"
                    style={{ borderLeft: `3px solid ${getClusterColorByHeight(session.maxheight)}` }}
                  >
                    <div
                      className="text-lg font-bold"
                      style={{ color: getClusterColorByHeight(session.maxheight) }}
                    >
                      {formatHeight(session.maxheight)}m
                    </div>
                    <div className="text-xs text-gray-500">Max</div>
                  </div>
                  <div
                    className="bg-gray-100 rounded-lg p-2"
                    style={{ borderLeft: `3px solid ${getClusterColorByHeight(session.lastheight)}` }}
                  >
                    <div
                      className="text-lg font-bold"
                      style={{ color: getClusterColorByHeight(session.lastheight) }}
                    >
                      {formatHeight(session.lastheight)}m
                    </div>
                    <div className="text-xs text-gray-500">Last</div>
                  </div>
                </div>

                {/* Kite & Time */}
                <div className="flex justify-between text-sm text-gray-600">
                  {session.kite && session.kite.kitebrand?.brand ? (
                    <span>
                      {session.kite.kitebrand.brand} {session.kite.kitesize?.size}m
                    </span>
                  ) : (
                    <span></span>
                  )}
                  <span>Duration: {formatSessionTime(session.sessiontime)}</span>
                </div>

                {/* Details Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowDetails(session);
                  }}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  View Details
                </button>
              </div>
            </Popup>
          </Marker>
        );
        })}
      </MarkerClusterGroup>
      )}

      {/* Spot Loader - fetches spots based on map bounds */}
      <SpotLoader
        onSpotsLoaded={handleSpotsLoaded}
        minZoomLevel={spotSettings.minZoomLevel}
        showSpots={spotSettings.showSpots}
      />

      {/* Spot Markers - no clustering, rendered behind user markers (both modes) */}
      {spotSettings.showSpots && spots.map((spot) => {
        // Count live users at this spot
        const liveUsersAtSpot = validSessions.filter(s => s.spot?.id === spot.id).length;
        // Get daylight info for the spot location
        const daylightInfo = getDaylightInfo(spot.latitude, spot.longitude);

        return (
          <Marker
            key={`spot-${spot.id}`}
            position={[spot.latitude, spot.longitude]}
            icon={createSpotMarkerIcon(spot.name, hoveredSpotId === spot.id)}
            zIndexOffset={-1000}
            eventHandlers={{
              mouseover: () => setHoveredSpotId(spot.id),
              mouseout: () => setHoveredSpotId(null),
            }}
          >
            <Popup className="spot-popup" maxWidth={250}>
              <div className="p-2">
                {/* Spot Header */}
                <div className="flex items-center gap-2 mb-2" title={`Kite spot: ${spot.name}`}>
                  <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 text-sm">{spot.name}</div>
                    {spot.meanreviews !== undefined && spot.meanreviews > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500" title={`Average rating: ${spot.meanreviews.toFixed(1)} stars from ${spot.nreviews || 0} reviews`}>
                        <svg className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                        </svg>
                        <span>{spot.meanreviews.toFixed(1)}</span>
                        {spot.nreviews !== undefined && (
                          <span className="text-gray-400">({spot.nreviews})</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Country with flag */}
                {spot.countryName && (
                  <div className="flex items-center gap-2 mb-2 bg-gray-50 rounded px-2 py-1.5" title={`Location: ${spot.countryName}`}>
                    {spot.alpha2 && (
                      <img
                        src={`https://flagcdn.com/w20/${spot.alpha2.toLowerCase()}.png`}
                        alt={spot.alpha2}
                        className="w-5 h-auto"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                    <span className="text-sm text-gray-700">{spot.countryName}</span>
                  </div>
                )}

                {/* Live Users Count with Stats - Clickable when riders present */}
                {(() => {
                  const spotSessions = validSessions.filter(s => s.spot?.id === spot.id);
                  const maxHeights = spotSessions.map(s => s.maxheight).filter(h => h > 0);
                  const highestLiveJump = maxHeights.length > 0 ? Math.max(...maxHeights) : 0;
                  const sortedHeights = [...maxHeights].sort((a, b) => b - a);
                  const top10Heights = sortedHeights.slice(0, 10);
                  const avgTop10 = top10Heights.length > 0
                    ? top10Heights.reduce((sum, h) => sum + h, 0) / top10Heights.length
                    : 0;

                  return liveUsersAtSpot > 0 ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSpotRidersModal({ spot, riders: spotSessions });
                      }}
                      className="w-full flex items-center gap-3 mb-2 rounded px-2 py-1.5 bg-blue-50 hover:bg-blue-100 transition-colors cursor-pointer"
                    >
                      {/* Riders count */}
                      <div className="flex items-center gap-1" title={`${liveUsersAtSpot} rider${liveUsersAtSpot > 1 ? 's' : ''} live now`}>
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-xs font-semibold text-blue-700">{liveUsersAtSpot}</span>
                      </div>
                      {/* Max height */}
                      <div className="flex items-center gap-1" title={`Highest live jump: ${highestLiveJump.toFixed(1)}m`}>
                        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                        <span className="text-xs font-semibold text-orange-600">{highestLiveJump.toFixed(1)}m</span>
                      </div>
                      {/* Avg top 10 */}
                      <div className="flex items-center gap-1" title={`Average of top 10 jumps: ${avgTop10.toFixed(1)}m`}>
                        <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <span className="text-xs font-semibold text-pink-600">{avgTop10.toFixed(1)}m</span>
                      </div>
                      <svg className="w-4 h-4 text-blue-400 flex-shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2 mb-2 rounded px-2 py-1.5 bg-gray-50">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-500">No riders live</span>
                    </div>
                  );
                })()}

                {/* Local Time & Daylight - Compact single line */}
                <div
                  className={`flex items-center gap-2 mb-2 rounded px-2 py-1.5 ${
                    daylightInfo.isDaylight ? 'bg-amber-50' : 'bg-indigo-50'
                  }`}
                  title={`Local time at spot: ${daylightInfo.localTimeString}. ${daylightInfo.isDaylight ? `Daylight - sunset at ${daylightInfo.sunsetTime}` : `Night time - sunrise at ${daylightInfo.sunriseTime}`}`}
                >
                  {daylightInfo.isDaylight ? (
                    <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                  )}
                  <span className={`text-sm font-medium ${daylightInfo.isDaylight ? 'text-amber-700' : 'text-indigo-700'}`}>
                    {daylightInfo.localTimeString}
                  </span>
                  <span className="text-gray-400">·</span>
                  <span className="text-xs text-gray-500">
                    {daylightInfo.isDaylight ? `Sunset ${daylightInfo.sunsetTime}` : `Sunrise ${daylightInfo.sunriseTime}`}
                  </span>
                </div>

                {/* Action Buttons - Side by Side */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                  <a
                    href={`https://discover.thesurfr.app/spots/${encodeURIComponent(spot.name.toLowerCase().replace(/\s+/g, '-'))}/${spot.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View spot details, reviews, and photos on Surfr Discover"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      flex: 1,
                      padding: '8px 10px',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      textDecoration: 'none',
                      boxShadow: '0 2px 6px rgba(37, 99, 235, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
                    }}
                  >
                    <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Discover
                  </a>
                  <a
                    href={`https://spot.thesurfr.app/dashboard?spot=${encodeURIComponent(spot.name)}&spotid=${spot.id}${spot.wgspotid ? `&wgspotid=${spot.wgspotid}` : ''}&showSpotName=true&windUnit=knots`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="View live wind conditions, forecasts, and statistics for this spot"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      flex: 1,
                      padding: '8px 10px',
                      background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '6px',
                      textDecoration: 'none',
                      boxShadow: '0 2px 6px rgba(249, 115, 22, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)';
                    }}
                  >
                    <svg style={{ width: '14px', height: '14px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    Dashboard
                  </a>
                </div>

                {/* Manage Spot Button (Admin only) */}
                {user?.isadmin && (
                  <button
                    title="Admin: Edit spot name, coordinates, merge with other spots"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSpotManagerModal(spot);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 12px',
                      background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 600,
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      marginBottom: '8px',
                      boxShadow: '0 2px 8px rgba(75, 85, 99, 0.3)',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #4b5563 0%, #374151 100%)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(75, 85, 99, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(75, 85, 99, 0.3)';
                    }}
                  >
                    <svg style={{ width: '16px', height: '16px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Manage Spot
                  </button>
                )}

                {/* Coordinates & Spot ID (for admins) */}
                <div className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1 flex justify-between items-center" title="GPS coordinates and unique spot identifier">
                  <span title={`Latitude: ${spot.latitude.toFixed(6)}, Longitude: ${spot.longitude.toFixed(6)}`}>{spot.latitude.toFixed(4)}, {spot.longitude.toFixed(4)}</span>
                  <span className="text-gray-400 font-mono" title="Unique database ID for this spot">ID: {spot.id}</span>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}

      {/* Spot Riders Modal - Rendered outside MapContainer via Portal */}
      {spotRidersModal && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setSpotRidersModal(null)}
        >
          <div
            style={{ backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', maxWidth: '480px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Dark background for better contrast */}
            <div style={{ background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)', padding: '20px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(239,68,68,0.4)' }}>
                    <svg style={{ width: '26px', height: '26px', color: 'white' }} viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                    </svg>
                  </div>
                  <div>
                    <h2 style={{ fontWeight: 700, fontSize: '20px', margin: 0, color: 'white' }}>{spotRidersModal.spot.name}</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', backgroundColor: '#22c55e', color: 'white', fontSize: '12px', fontWeight: 600, padding: '2px 8px', borderRadius: '12px' }}>
                        <span style={{ width: '6px', height: '6px', backgroundColor: 'white', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
                        {spotRidersModal.riders.length} LIVE
                      </span>
                      {spotRidersModal.spot.countryName && spotRidersModal.spot.alpha2 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9ca3af', fontSize: '13px' }}>
                          <img
                            src={`https://flagcdn.com/w20/${spotRidersModal.spot.alpha2.toLowerCase()}.png`}
                            alt=""
                            style={{ width: '16px', height: 'auto', borderRadius: '2px' }}
                          />
                          {spotRidersModal.spot.countryName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSpotRidersModal(null)}
                  style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', cursor: 'pointer', transition: 'background 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                >
                  <svg style={{ width: '20px', height: '20px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Jump Stats Bar */}
              {(() => {
                const maxHeights = spotRidersModal.riders.map(r => r.maxheight).filter(h => h > 0);
                const highestJump = maxHeights.length > 0 ? Math.max(...maxHeights) : 0;
                const sortedHeights = [...maxHeights].sort((a, b) => b - a);
                const top10Heights = sortedHeights.slice(0, 10);
                const avgTop10 = top10Heights.length > 0
                  ? top10Heights.reduce((sum, h) => sum + h, 0) / top10Heights.length
                  : 0;

                return maxHeights.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '12px', padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg style={{ width: '18px', height: '18px', color: '#f97316' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                      <div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Highest</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#f97316' }}>{highestJump.toFixed(1)}m</div>
                      </div>
                    </div>
                    <div style={{ width: '1px', height: '32px', backgroundColor: 'rgba(255,255,255,0.2)' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg style={{ width: '18px', height: '18px', color: '#ec4899' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Avg Top 10</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#ec4899' }}>{avgTop10.toFixed(1)}m</div>
                      </div>
                    </div>
                    <div style={{ width: '1px', height: '32px', backgroundColor: 'rgba(255,255,255,0.2)' }}></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <svg style={{ width: '18px', height: '18px', color: '#22c55e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <div style={{ fontSize: '10px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Riders</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e' }}>{spotRidersModal.riders.length}</div>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            {/* Riders List */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '8px' }}>
              {spotRidersModal.riders.map((rider) => {
                const sessionMinutes = Math.floor(rider.sessiontime / 60);
                const sessionHours = Math.floor(sessionMinutes / 60);
                const sessionTimeStr = sessionHours > 0
                  ? `${sessionHours}h ${sessionMinutes % 60}m`
                  : `${sessionMinutes}m`;

                return (
                  <button
                    key={rider.id}
                    onClick={() => {
                      setSpotRidersModal(null);
                      onShowDetails(rider);
                    }}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #e5e7eb', background: 'white', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', marginBottom: '6px', transition: 'all 0.2s' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'white';
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }}
                  >
                    {/* Profile Picture with live indicator */}
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <img
                        src={rider.user.profilepicid
                          ? `${ENDPOINTS.IMAGES.PROFILE}/${rider.user.profilepicid}`
                          : DEFAULT_PROFILE_PIC
                        }
                        alt={rider.user.name}
                        style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #22c55e' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = DEFAULT_PROFILE_PIC;
                        }}
                      />
                      {/* Live pulse indicator */}
                      <div style={{ position: 'absolute', bottom: '0', right: '0', width: '14px', height: '14px', backgroundColor: '#22c55e', borderRadius: '50%', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {rider.isstreaming && (
                          <svg style={{ width: '8px', height: '8px', color: 'white' }} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                          </svg>
                        )}
                      </div>
                    </div>

                    {/* Name & badges */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rider.user.name}</span>
                        {rider.user.country && (
                          <img
                            src={`https://flagcdn.com/w20/${rider.user.country.toLowerCase()}.png`}
                            alt={rider.user.country}
                            style={{ width: '16px', height: 'auto' }}
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                          />
                        )}
                        {rider.user.ispro && (
                          <span style={{ backgroundColor: '#fbbf24', fontSize: '9px', fontWeight: 700, padding: '1px 4px', borderRadius: '3px', color: '#78350f' }}>PRO</span>
                        )}
                        {rider.isstreaming && (
                          <span style={{ backgroundColor: '#ef4444', fontSize: '9px', fontWeight: 600, padding: '1px 4px', borderRadius: '3px', color: 'white' }}>LIVE</span>
                        )}
                      </div>
                      {/* Compact stats row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px', fontSize: '12px' }}>
                        <span style={{ color: getClusterColorByHeight(rider.maxheight), fontWeight: 600 }}>{rider.maxheight.toFixed(1)}m</span>
                        <span style={{ color: '#d1d5db' }}>/</span>
                        <span style={{ color: getClusterColorByHeight(rider.lastheight), fontWeight: 500 }}>{rider.lastheight.toFixed(1)}m</span>
                        <span style={{ color: '#d1d5db' }}>•</span>
                        <span style={{ color: '#6b7280' }}>{sessionTimeStr}</span>
                        {/* Device info */}
                        {rider.device && (
                          <>
                            <span style={{ color: '#d1d5db' }}>•</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                              {/* Device type icon */}
                              {(rider.device.os?.toLowerCase().includes('watch') || rider.device.os?.toLowerCase().includes('wear') || rider.device.brand?.toLowerCase().includes('garmin')) ? (
                                <svg style={{ width: '12px', height: '12px', color: '#f97316' }} viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                                </svg>
                              ) : (
                                <svg style={{ width: '12px', height: '12px', color: '#9ca3af' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                                </svg>
                              )}
                              {/* OS icon */}
                              {(rider.device.brand?.toLowerCase().includes('apple') || rider.device.os?.toLowerCase().includes('ios')) ? (
                                <svg style={{ width: '10px', height: '10px', color: '#d1d5db' }} viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                                </svg>
                              ) : (
                                <svg style={{ width: '10px', height: '10px', color: '#22c55e' }} viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5z"/>
                                </svg>
                              )}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Arrow */}
                    <svg style={{ width: '16px', height: '16px', color: '#9ca3af', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Spot Manager Modal (Admin only) */}
      {spotManagerModal && (
        <SpotManagerModal
          spot={spotManagerModal}
          onClose={() => setSpotManagerModal(null)}
          onSpotUpdated={() => {
            // Refresh spots by triggering a re-load
            setSpots([]);
          }}
        />
      )}

      {/* Request Map Markers (Request mode only) */}
      {viewMode === "requests" && spotRequests.length > 0 && onRequestSelect && onReviewRequest && (
        <RequestMapMarkers
          requests={spotRequests}
          selectedRequest={selectedRequest}
          hoveredRequest={hoveredRequest}
          onRequestSelect={onRequestSelect}
          onReview={onReviewRequest}
          onSwap={onSwapRequest}
          onShowSpotStats={(spotInfo) => {
            // Create a minimal MapSpot object to open SpotManagerModal
            const mapSpot: MapSpot = {
              id: spotInfo.id,
              name: spotInfo.name,
              latitude: spotInfo.latitude ?? 0,
              longitude: spotInfo.longitude ?? 0,
            };
            setSpotManagerModal(mapSpot);
          }}
        />
      )}

      {/* Add Spot Mode: click handler + draggable marker + nearby spots */}
      {addSpotMode && <AddSpotMapClickHandler onClick={onMapClickForSpot} />}
      {addSpotMode && addSpotLocation && (
        <>
          <Marker
            position={[addSpotLocation.lat, addSpotLocation.lng]}
            icon={L.divIcon({
              className: "add-spot-marker",
              html: `<div style="display:flex;flex-direction:column;align-items:center;">
                <svg viewBox="0 0 24 32" width="36" height="44" style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.4));">
                  <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#16a34a"/>
                  <line x1="8" y1="11" x2="16" y2="11" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
                  <line x1="12" y1="7" x2="12" y2="15" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
                </svg>
              </div>`,
              iconSize: [36, 44],
              iconAnchor: [18, 44],
            })}
            draggable
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const pos = marker.getLatLng();
                onAddSpotDrag?.(pos.lat, pos.lng);
              },
            }}
          />
          {/* Nearby spot markers (red) for reference */}
          {addSpotNearbySpots.map((spot) => (
            <Marker
              key={`nearby-${spot.id}`}
              position={[spot.latitude, spot.longitude]}
              icon={L.divIcon({
                className: "nearby-ref-marker",
                html: `<div style="display:flex;flex-direction:column;align-items:center;">
                  <svg viewBox="0 0 24 32" width="20" height="26" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
                    <path d="M12 0C5.4 0 0 5.4 0 12c0 7.2 12 20 12 20s12-12.8 12-20c0-6.6-5.4-12-12-12z" fill="#ef4444"/>
                    <circle cx="12" cy="11" r="4" fill="white"/>
                  </svg>
                  <div style="background:rgba(0,0,0,0.7);color:white;padding:1px 4px;border-radius:2px;font-size:9px;white-space:nowrap;margin-top:-2px;">${spot.name.length > 14 ? spot.name.substring(0, 13) + "…" : spot.name}</div>
                </div>`,
                iconSize: [80, 36],
                iconAnchor: [40, 26],
              })}
            />
          ))}
        </>
      )}
    </MapContainer>
  );
};

export default React.memo(LiveMap);
